import clsx from 'clsx';

import { SDSInputErrorMessage } from '@repo/sds/SDSInputErrorMessage';
import type { CropMeta } from '@repo/ui/components/elements/Cropper/Cropper';
import { IMAGE_UPLOAD_ACCEPT, IMAGE_UPLOAD_MAX_FILE_SIZE_BYTES } from '@repo/ui/constants/image-upload';

import { ImageCropperModal } from '../ImageCropperModal/ImageCropperModal';
import {
  canRemoveImage,
  hasEditableOriginal,
  resolveCropperImageUrl,
  resolveImageInputProps,
} from './image-input-utils';
import { ImageInputBody } from './image-input-views';
import { useImageInput } from './use-image-input';

import './ImageInput.scss';

export interface ImageData {
  original?: File;
  thumbnail?: File;
  meta: CropMeta | undefined;
  url?: string;
  originalUrl?: string;
  originalId?: string;
  thumbnailId?: string;
}

type AspectRatio = 'square' | '16:9';

export interface ImageInputProps {
  value: ImageData | null;
  onChange: (imageData: ImageData | null) => void | Promise<void>;
  displayName?: string;
  aspectRatio: AspectRatio;
  dataTestId?: string;
  previewDataTestId?: string;
  previewAlt?: string;
  allowRemove?: boolean;
  allowCommittedRemove?: boolean;
  isDisabled?: boolean;
  editDataTestId?: string;
  replaceDataTestId?: string;
  removeDataTestId?: string;
  displayNameDataTestId?: string;
  errorDataTestId?: string;
  formFieldName?: string;
  presentation?: 'default' | 'profile';
  profileContentDataTestId?: string;
}

export type ResolvedImageInputProps = Omit<
  ImageInputProps,
  | 'displayName'
  | 'previewDataTestId'
  | 'editDataTestId'
  | 'removeDataTestId'
  | 'previewAlt'
  | 'allowRemove'
  | 'allowCommittedRemove'
  | 'isDisabled'
  | 'errorDataTestId'
  | 'presentation'
> & {
  displayName: string;
  previewDataTestId: string;
  editDataTestId: string;
  removeDataTestId: string;
  previewAlt: string;
  allowRemove: boolean;
  allowCommittedRemove: boolean;
  isDisabled: boolean;
  errorDataTestId: string;
  presentation: 'default' | 'profile';
};

export function ImageInput(props: ImageInputProps) {
  const {
    value,
    onChange,
    displayName,
    aspectRatio,
    dataTestId,
    previewDataTestId,
    previewAlt,
    allowRemove,
    allowCommittedRemove,
    isDisabled,
    editDataTestId,
    replaceDataTestId,
    removeDataTestId,
    displayNameDataTestId,
    errorDataTestId,
    formFieldName,
    presentation,
    profileContentDataTestId,
  } = resolveImageInputProps(props);
  const {
    isModalOpen,
    inputRootRef,
    src,
    previewUrl,
    initialCropMeta,
    uploadError,
    setUploadError,
    openCropper,
    editImage,
    handleCropComplete,
    handleCloseModal,
    handleDelete,
  } = useImageInput({ value, onChange, isDisabled });

  const fileInputProps = {
    accept: IMAGE_UPLOAD_ACCEPT,
    onChange: openCropper,
    onError: setUploadError,
    maxFileSizeBytes: IMAGE_UPLOAD_MAX_FILE_SIZE_BYTES,
    isDisabled,
  };
  const canRemove = canRemoveImage(value, allowRemove, allowCommittedRemove);
  const canEdit = hasEditableOriginal(value);
  const cropperImageUrl = resolveCropperImageUrl(previewUrl, value);

  return (
    <div
      ref={inputRootRef}
      className={clsx('ImageInput', aspectRatio === 'square' && 'ImageInput--square')}
      aria-disabled={isDisabled}
    >
      <ImageInputBody
        presentation={presentation}
        src={src}
        previewAlt={previewAlt}
        previewDataTestId={previewDataTestId}
        displayName={displayName}
        displayNameDataTestId={displayNameDataTestId}
        profileContentDataTestId={profileContentDataTestId}
        dataTestId={dataTestId}
        replaceDataTestId={replaceDataTestId}
        formFieldName={formFieldName}
        fileInputProps={fileInputProps}
        canRemove={canRemove}
        canEdit={canEdit}
        isDisabled={isDisabled}
        editDataTestId={editDataTestId}
        removeDataTestId={removeDataTestId}
        onEdit={editImage}
        onDelete={handleDelete}
      />
      <SDSInputErrorMessage errorMessage={uploadError ?? undefined} dataTestId={errorDataTestId} />
      <ImageCropperModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleCropComplete}
        imageUrl={cropperImageUrl}
        initialMeta={initialCropMeta}
        aspectRatio={aspectRatio}
        onReplace={openCropper}
        onReplaceError={setUploadError}
        errorMessage={uploadError}
        isDisabled={isDisabled}
      />
    </div>
  );
}
